"use client";

import React, { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "@/lib/icons";
import Image from "next/image";

// Custom hook for touch gestures
function useSwipe(onSwipeLeft?: () => void, onSwipeRight?: () => void) {
  const touchStart = useRef<number | null>(null);
  const touchEnd = useRef<number | null>(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    touchEnd.current = null;
    touchStart.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    touchEnd.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (!touchStart.current || !touchEnd.current) return;
    
    const distance = touchStart.current - touchEnd.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && onSwipeLeft) onSwipeLeft();
    if (isRightSwipe && onSwipeRight) onSwipeRight();
  };

  return { onTouchStart, onTouchMove, onTouchEnd };
}

const galleryImages = [
  {
    id: 1,
    src: "/gallery/installation-1.jpg",
    alt: "Professional installation of commercial washing machines in modern laundry facility",
  },
  {
    id: 2,
    src: "/gallery/installation-2.jpg",
    alt: "Complete laundry room setup with multiple commercial washers and dryers",
  },
  {
    id: 3,
    src: "/gallery/wesley-spearing.jpeg",
    alt: "Wesley Spearing, Commercial Sales Manager at DPCLR",
  },
  {
    id: 4,
    src: "/gallery/installation-3.jpg",
    alt: "Industrial-grade laundry equipment installation for high-capacity operations",
  },
  {
    id: 5,
    src: "/gallery/installation-4.jpg",
    alt: "State-of-the-art commercial laundry facility installation and setup",
  },
  {
    id: 6,
    src: "/gallery/installation-5.jpg",
    alt: "Professional commercial laundry equipment installation by DPCLR technicians",
  },
  {
    id: 7,
    src: "/gallery/service-1.jpg",
    alt: "Expert maintenance service on commercial washing and drying equipment",
  },
  {
    id: 8,
    src: "/gallery/service-2.jpg",
    alt: "Routine preventive maintenance ensuring optimal equipment performance",
  },
  {
    id: 9,
    src: "/gallery/service-3.jpg",
    alt: "Emergency repair service and diagnostics on commercial laundry systems",
  },
  {
    id: 10,
    src: "/gallery/service-4.jpg",
    alt: "Professional service technician performing comprehensive equipment maintenance",
  },
];

export function GallerySection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const nextImage = useCallback(() => {
    const newIndex = (currentIndex + 1) % galleryImages.length;
    setCurrentIndex(newIndex);
    // Announce the change to screen readers
    announceCurrentImage(newIndex);
  }, [currentIndex]);

  const prevImage = useCallback(() => {
    const newIndex = (currentIndex - 1 + galleryImages.length) % galleryImages.length;
    setCurrentIndex(newIndex);
    // Announce the change to screen readers
    announceCurrentImage(newIndex);
  }, [currentIndex]);

  const announceCurrentImage = (index: number) => {
    const announcement = `Showing image ${index + 1} of ${galleryImages.length}: ${galleryImages[index].alt}`;
    // Create a temporary element to announce the change
    const announcementElement = document.createElement('div');
    announcementElement.setAttribute('aria-live', 'polite');
    announcementElement.setAttribute('aria-atomic', 'true');
    announcementElement.className = 'sr-only';
    announcementElement.textContent = announcement;
    document.body.appendChild(announcementElement);
    
    // Remove the element after announcement
    setTimeout(() => {
      document.body.removeChild(announcementElement);
    }, 1000);
  };

  const { onTouchStart, onTouchMove, onTouchEnd } = useSwipe(nextImage, prevImage);

  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      nextImage();
    }, 4000); // Optimal 4s timing - engaging but not rushed

    return () => clearInterval(interval);
  }, [isPaused, nextImage]);

  return (
    <section 
      id="gallery"
      aria-labelledby="gallery-heading"
      className="py-20 bg-gradient-to-b from-white via-gray-50/50 to-white overflow-hidden"
    >
      <div className="text-center mb-16 sm:mb-12 px-4">
        <span className="text-blue-600 font-semibold text-sm sm:text-base uppercase tracking-wider mb-4 sm:mb-4 block" aria-hidden="true">
          Our Work
        </span>
        <h2 id="gallery-heading" className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-4">
          Installations & Service Gallery
        </h2>
        <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto" role="doc-subtitle">
          See our professional team in action, delivering quality installations and maintenance services across the UK
        </p>
      </div>

      {/* Desktop infinite flowing gallery */}
      <div 
        className="hidden md:block w-screen relative left-[50%] right-[50%] ml-[-50vw] mr-[-50vw]"
        role="region"
        aria-label="Desktop gallery"
      >
        <div 
          className="relative overflow-hidden py-4"
          role="marquee"
          aria-roledescription="Scrolling gallery"
        >
          <div 
            className="flex transition-transform duration-700 ease-in-out" 
            style={{
              transform: `translateX(-${currentIndex * 320}px)` // Adjust based on image width + gap
            }}
            role="presentation"
          >
            {/* First set */}
            {galleryImages.map((image) => (
              <div
                key={`${image.id}-1`}
                className="flex-shrink-0 mx-3 md:mx-4"
                role="group"
                aria-label={`Gallery image ${image.id}: ${image.alt}`}
              >
                <div
                  className="relative w-[420px] md:w-[500px] lg:w-[600px] h-[315px] md:h-[375px] lg:h-[450px] rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 overflow-hidden bg-gray-100"
                  role="img"
                  aria-label={image.alt}
                  tabIndex={0}
                  style={{
                    backgroundImage: `url(${image.src})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }}
                />
              </div>
            ))}
            
            {/* Duplicate set for seamless loop */}
            {galleryImages.map((image) => (
              <div
                key={`${image.id}-2`}
                className="flex-shrink-0 mx-3 md:mx-4"
                aria-hidden="true"
              >
                <div 
                  className="relative w-[420px] md:w-[500px] lg:w-[600px] h-[315px] md:h-[375px] lg:h-[450px] rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden bg-gray-100"
                  style={{
                    backgroundImage: `url(${image.src})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }}
                  aria-hidden="true"
                />
              </div>
            ))}
          </div>
          
          {/* Navigation controls */}
          <div 
            className="absolute inset-y-0 left-0 flex items-center"
            role="group"
            aria-label="Gallery navigation"
          >
            <Button
              variant="outline"
              size="icon"
              className="rounded-full bg-white/80 hover:bg-white ml-4 w-12 h-12 min-w-12 min-h-12"
              onClick={prevImage}
              aria-label="Previous image"
              type="button"
            >
              <ChevronLeft className="h-6 w-6" aria-hidden="true" />
            </Button>
          </div>
          <div className="absolute inset-y-0 right-0 flex items-center">
            <Button
              variant="outline"
              size="icon"
              className="rounded-full bg-white/80 hover:bg-white mr-4 w-12 h-12 min-w-12 min-h-12"
              onClick={nextImage}
              aria-label="Next image"
              type="button"
            >
              <ChevronRight className="h-6 w-6" aria-hidden="true" />
            </Button>
          </div>

          {/* Gradient overlays */}
          <div className="absolute top-0 left-0 w-32 h-full bg-gradient-to-r from-white via-white/90 to-transparent z-10 pointer-events-none" aria-hidden="true" />
          <div className="absolute top-0 right-0 w-32 h-full bg-gradient-to-l from-white via-white/90 to-transparent z-10 pointer-events-none" aria-hidden="true" />
        </div>
      </div>

      {/* Desktop Navigation Controls */}
      <div className="hidden md:block px-4">
        <div 
          className="flex justify-center items-center mt-6 gap-4"
          role="group"
          aria-label="Gallery navigation"
        >
          <Button
            variant="outline"
            size="icon"
            className="rounded-full hover:bg-blue-50 border-blue-200 hover:border-blue-300 transition-all duration-200"
            onClick={() => {
              prevImage();
              setIsPaused(true);
              setTimeout(() => setIsPaused(false), 8000);
            }}
            aria-label="Previous image"
            type="button"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </Button>
          <div 
            className="flex gap-2 px-1"
            role="tablist"
            aria-label="Select image"
          >
            {galleryImages.map((_, index) => (
              <button
                key={`desktop-tab-${index}`}
                type="button"
                role="tab"
                tabIndex={index === currentIndex ? 0 : -1}
                aria-selected={index === currentIndex ? "true" : "false"}
                aria-label={`Go to image ${index + 1}`}
                aria-controls={`gallery-image-${index + 1}`}
                className={`min-w-8 min-h-8 w-8 h-8 p-2 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center ${
                  index === currentIndex 
                    ? 'bg-blue-50 ring-1 ring-blue-200' 
                    : 'hover:bg-blue-50/50'
                }`}
                onClick={() => {
                  setCurrentIndex(index);
                  setIsPaused(true);
                  setTimeout(() => setIsPaused(false), 8000);
                }}
              >
                <div
                  className={`rounded-full transition-all duration-300 ${
                    index === currentIndex 
                      ? 'bg-blue-600 w-3 h-1.5' 
                      : 'bg-blue-200 w-1.5 h-1.5 hover:bg-blue-300'
                  }`}
                  aria-hidden="true"
                />
                <span className="sr-only">{index === currentIndex ? 'Current image' : `Image ${index + 1}`}</span>
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="icon"
            className="rounded-full hover:bg-blue-50 border-blue-200 hover:border-blue-300 transition-all duration-200"
            onClick={() => {
              nextImage();
              setIsPaused(true);
              setTimeout(() => setIsPaused(false), 8000);
            }}
            aria-label="Next image"
            type="button"
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>

        {/* Desktop Controls and status */}
        <div className="mt-4 flex flex-col items-center gap-3">
          <div className="flex items-center gap-4">
            <button
              type="button"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              onClick={() => setIsPaused(!isPaused)}
              aria-label={isPaused ? "Resume slideshow" : "Pause slideshow"}
            >
              {isPaused ? "Resume" : "Pause"}
            </button>
            <div 
              className="text-sm text-gray-600"
              aria-live="polite"
              aria-atomic="true"
            >
              Image {currentIndex + 1} of {galleryImages.length}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile gallery carousel */}
      <div 
        className="md:hidden px-4"
        role="region"
        aria-label="Mobile gallery"
      >
        <div 
          className="relative overflow-hidden"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          role="group"
          aria-roledescription="carousel"
          aria-label="Gallery images"
          aria-live="polite"
          aria-atomic="false"
        >
          <div 
            ref={containerRef}
            className="flex transition-transform duration-500 ease-out touch-pan-x"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            role="presentation"
          >
            {galleryImages.map((image, index) => (
              <div 
                key={image.id}
                id={`gallery-image-${index + 1}`}
                className="flex-shrink-0 w-full"
                style={{ width: '100%' }}
                role="group"
                aria-roledescription="slide"
                aria-label={`${index + 1} of ${galleryImages.length}: ${image.alt}`}
                aria-hidden={currentIndex !== index}
              >
                <div
                  className="aspect-[4/3] w-full rounded-xl shadow-xl hover:shadow-2xl transition-shadow duration-300 overflow-hidden bg-gray-100"
                  role="img"
                  aria-label={image.alt}
                  style={{
                    backgroundImage: `url(${image.src})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }}
                />
              </div>
            ))}
          </div>

          {/* Navigation controls - Always visible */}
          <div 
            className="flex justify-center items-center mt-6 gap-4"
            role="group"
            aria-label="Gallery navigation"
          >
            <Button
              variant="outline"
              size="icon"
              className="rounded-full hover:bg-blue-50 border-blue-200 hover:border-blue-300 transition-all duration-200"
              onClick={() => {
                prevImage();
                setIsPaused(true);
                setTimeout(() => setIsPaused(false), 8000); // Resume auto-play after 8s
              }}
              aria-label="Previous image"
              type="button"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
            <div 
              className="flex gap-2 px-1"
              role="tablist"
              aria-label="Select image"
            >
              {galleryImages.map((_, index) => (
                <button
                  key={`tab-${index}`}
                  type="button"
                  role="tab"
                  tabIndex={index === currentIndex ? 0 : -1}
                  aria-selected={index === currentIndex ? "true" : "false"}
                  aria-label={`Go to image ${index + 1}`}
                  aria-controls={`gallery-image-${index + 1}`}
                  className={`min-w-8 min-h-8 w-8 h-8 p-2 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center ${
                    index === currentIndex 
                      ? 'bg-blue-50 ring-1 ring-blue-200' 
                      : 'hover:bg-blue-50/50'
                  }`}
                  onClick={() => {
                    setCurrentIndex(index);
                    setIsPaused(true);
                    setTimeout(() => setIsPaused(false), 8000); // Resume auto-play after 8s
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setCurrentIndex(index);
                      setIsPaused(true);
                      setTimeout(() => setIsPaused(false), 8000);
                    } else if (e.key === 'ArrowRight') {
                      e.preventDefault();
                      const nextIndex = (index + 1) % galleryImages.length;
                      const nextButton = document.querySelector(`button[aria-controls="gallery-image-${nextIndex + 1}"]`) as HTMLButtonElement;
                      nextButton?.focus();
                    } else if (e.key === 'ArrowLeft') {
                      e.preventDefault();
                      const prevIndex = (index - 1 + galleryImages.length) % galleryImages.length;
                      const prevButton = document.querySelector(`button[aria-controls="gallery-image-${prevIndex + 1}"]`) as HTMLButtonElement;
                      prevButton?.focus();
                    }
                  }}
                >
                  <div
                    className={`rounded-full transition-all duration-300 ${
                      index === currentIndex 
                        ? 'bg-blue-600 w-3 h-1.5' 
                        : 'bg-blue-200 w-1.5 h-1.5 hover:bg-blue-300'
                    }`}
                    aria-hidden="true"
                  />
                  <span className="sr-only">{index === currentIndex ? 'Current image' : `Image ${index + 1}`}</span>
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              size="icon"
              className="rounded-full hover:bg-blue-50 border-blue-200 hover:border-blue-300 transition-all duration-200"
              onClick={() => {
                nextImage();
                setIsPaused(true);
                setTimeout(() => setIsPaused(false), 8000); // Resume auto-play after 8s
              }}
              aria-label="Next image"
              type="button"
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>

        {/* Controls and status */}
        <div className="mt-4 flex flex-col items-center gap-3">
          <div className="flex items-center gap-4">
            <button
              type="button"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              onClick={() => setIsPaused(!isPaused)}
              aria-label={isPaused ? "Resume slideshow" : "Pause slideshow"}
            >
              {isPaused ? "Resume" : "Pause"}
            </button>
            <div 
              className="text-sm text-gray-600"
              aria-live="polite"
              aria-atomic="true"
            >
              Image {currentIndex + 1} of {galleryImages.length}
            </div>
          </div>
          <div 
            className="text-center text-sm text-gray-500 md:hidden"
            role="status"
            aria-live="polite"
          >
            Swipe to see more images
          </div>
        </div>
      </div>
    </section>
  );
}
